import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, UserPlus, Users, X as XIcon } from "lucide-react";

/**
 * Picker bi-modale: cerca paziente esistente (autocomplete) o crea nuovo.
 * onSelect(patient | null) — patient è oggetto con tutti i campi anagrafici;
 * null = modalità "nuovo paziente".
 */
export default function PatientPicker({ mode, setMode, selected, onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode !== "existing") return;
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/medico/patients", { params: q ? { q, limit: 30 } : { limit: 30 } });
        setResults(data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [q, mode]);

  return (
    <div className="mb-4" data-testid="patient-picker">
      <div className="inline-flex p-1 bg-slate-100 rounded-xl mb-3">
        <button type="button" data-testid="picker-existing" onClick={() => { setMode("existing"); onSelect(null); }}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 ${mode === "existing" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500"}`}>
          <Users size={13} /> Paziente esistente
        </button>
        <button type="button" data-testid="picker-new" onClick={() => { setMode("new"); onSelect(null); }}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 ${mode === "new" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500"}`}>
          <UserPlus size={13} /> Nuovo paziente
        </button>
      </div>
      {mode === "existing" && (
        <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
          <div className="relative bg-white border-b border-[#E2E8F0]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input data-testid="picker-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, cognome, CF, telefono, email, nascita…" className="w-full pl-9 pr-3 py-2.5 text-sm focus:outline-none" />
          </div>
          {selected && (
            <div className="bg-brand-azure/10 border-b border-brand-azure/30 px-3 py-2 flex items-center justify-between" data-testid="picker-selected">
              <div className="text-xs">
                <div className="font-extrabold text-brand-navy">{selected.patient_full_name}</div>
                <div className="text-slate-500 font-mono">{selected.patient_fiscal_code || "—"} · {selected.patient_birth || "—"} · {selected.visits_count} visit{selected.visits_count === 1 ? "a" : "e"}</div>
              </div>
              <button type="button" onClick={() => onSelect(null)} className="text-slate-500 hover:text-red-600"><XIcon size={14} /></button>
            </div>
          )}
          <div className="max-h-48 overflow-y-auto bg-white" data-testid="picker-results">
            {loading ? <div className="p-3 text-xs text-slate-400 text-center">Cerco…</div> : results.length === 0 ? (
              <div className="p-3 text-xs text-slate-400 text-center">Nessun paziente — usa "Nuovo paziente" qui sopra.</div>
            ) : results.map((p, idx) => (
              <button key={idx} type="button" data-testid={`picker-item-${idx}`} onClick={() => onSelect(p)} className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-[#E2E8F0] last:border-b-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-brand-navy text-sm truncate">{p.patient_full_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{p.patient_fiscal_code || "—"} · {p.patient_birth || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{p.patient_phone || ""}{p.patient_phone && p.patient_email ? " · " : ""}{p.patient_email || ""}</div>
                  </div>
                  <div className="text-[10px] text-slate-400 text-right whitespace-nowrap">
                    <div>Ultima:</div>
                    <div>{new Date(p.last_visit_at).toLocaleDateString("it-IT")}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
